import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Select, Input, Radio } from "antd";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import Swal from "sweetalert2";
import usePermissions from "../../../hook/usePermissions";
import {
    GetProcedimientoSeguro,
    FacturarAseguradora,
    CancelarResponsabilidadSeguro,
} from "../../../Service/Service.porcedimientos";
import { ListaruserSinPagineo } from "../../../Service/Service.users";
import DataTable from "../../UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../../UI/DataTable/piezas";
import { ChipEstado } from "../../UI/Chip";
import Boton from "../../UI/Boton";
import { Aviso, useToast } from "../../UI/Aviso";
import { IconoDocumentoHecho, IconoProhibido } from "../../UI/Iconos";

// COBERTURAS-04 -- pestaña "Pendiente de facturar": selección múltiple +
// "Facturar a aseguradora" (FAC agrupada) + "Cancelar responsabilidad".

// SISTEMA-DE-DISENO: el estado usa el color que el resto del sistema da a ese
// significado. "Por Cobrar" es trabajo pendiente, no un error.
const TONO_ESTADO = { "Por Cobrar": "pendiente", Facturado: "hecho", Eliminado: "anulado", Cancelado: "anulado" };

// El rojo del botón destructivo de Swal se LEE del tema en vez de escribirse a
// mano: así cambia con el tema como el resto del sistema, y no queda un color
// suelto en un archivo de pantalla.
const rojoDelSistema = () =>
    (typeof getComputedStyle === "function" &&
        getComputedStyle(document.documentElement).getPropertyValue("--color-danger").trim()) ||
    undefined;

function GridFacturaListadoPorCobrar({ data }) {
    const { hasPermission } = usePermissions();
    const { filters } = data;
    const { mostrar } = useToast();

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);

    const [selectedKeys, setSelectedKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [vendedores, setVendedores] = useState([]);
    const [vendedorId, setVendedorId] = useState(null);
    const [modo, setModo] = useState("DETALLADA");
    // INFO-ADICIONAL-SEGURO-65: mismo campo que la factura normal
    // (facturas.info_adicional_1). No es un comentario interno: Contífico lo
    // usa como descripción del documento, así que es lo que la aseguradora lee
    // en el RIDE.
    const [infoAdicional, setInfoAdicional] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [emitida, setEmitida] = useState(null);

    const num = (v) => Number(v || 0);
    const money = (v) => `$${num(v).toFixed(2)}`;

    const fetchData = useCallback(async (p = 1, ps = 20) => {
        setLoading(true);
        const result = await GetProcedimientoSeguro({ page: p, limit: ps, filters: filters || {} });
        // OJO: este endpoint devuelve `success: false` TAMBIÉN cuando la
        // consulta va bien (ver ProcedimientoPrecio.ts, ListarProcedimientoSeguros:
        // el camino feliz retorna success:false, message:null). Por eso el
        // código original preguntaba `!result.success` -- y por eso NO podía
        // distinguir un fallo real: los dos casos entraban por la misma rama.
        // Mientras el backend no arregle su bandera, lo que sí discrimina es la
        // presencia de `pagination`, que el camino feliz siempre trae y el de
        // error nunca. No se toca el contrato del servidor.
        const consultaOk = Boolean(result && result.pagination);
        if (consultaOk) {
            setRows((result.data || []).map((it) => ({ ...it, key: it.idprocedimiento_cita_paciente })));
            setError(null);
            setTotal(result.pagination?.total || 0);
            setPage(result.pagination?.page || 1);
            setPageSize(result.pagination?.limit || ps);
        } else {
            setRows([]);
            setTotal(0);
            setError(result?.message || "No se pudo obtener las responsabilidades de seguro.");
        }
        // COBERTURAS-04 §8: al cambiar el conjunto (filtros/página) se limpia la
        // selección -> nunca se facturan responsabilidades que el usuario no ve.
        setSelectedKeys([]);
        setSelectedRows([]);
        setLoading(false);
    }, [filters]);

    useEffect(() => {
        fetchData(1, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // resumen de la selección (el backend SIEMPRE recalcula; esto es preview)
    const resumen = useMemo(() => {
        const segs = new Set(selectedRows.map((r) => r.Seguros?.idseguro ?? r.seguro_id));
        const pacientes = new Set(selectedRows.map((r) => r.paciente?.idpaciente ?? r.idpaciente));
        const totalSel = selectedRows.reduce((a, r) => a + num(r.precio) * num(r.cantidad), 0);
        // preview de líneas en modo resumida: agrupa por (servicio, precio)
        const gruposResumida = new Set(
            selectedRows.map((r) => `${r.ExamenTipo?.IdExamenTipo ?? r.idExamenTipo}|${num(r.precio).toFixed(4)}`)
        );
        return {
            count: selectedRows.length,
            segurosDistintos: segs.size,
            aseguradora: selectedRows[0]?.Seguros?.nombre || "",
            pacientes: pacientes.size,
            total: totalSel,
            lineasDetallada: selectedRows.length,
            lineasResumida: gruposResumida.size,
        };
    }, [selectedRows]);

    const columns = [
        {
            key: "paciente",
            titulo: "Paciente",
            ancho: 230,
            render: (r) => (
                <CeldaDoble
                    principal={
                        r.paciente?.nombres_completos ? tituloDeNombre(r.paciente.nombres_completos) : "Sin determinar"
                    }
                    secundario={[r.paciente?.cedula, r.fecha_emision ? format(new Date(r.fecha_emision), "dd/MM/yyyy", { locale: es }) : "—"]
                        .filter(Boolean)
                        .join(" · ")}
                />
            ),
        },
        {
            // El servicio y cuántas veces se prestó son un solo dato: el
            // valor de la fila es el producto de los dos.
            key: "servicio",
            titulo: "Servicio",
            ancho: 260,
            render: (r) => (
                <CeldaDoble
                    principal={r.ExamenTipo?.descripcion}
                    secundario={`Cant. ${num(r.cantidad).toFixed(2)}`}
                />
            ),
        },
        { key: "aseguradora", titulo: "Aseguradora", ancho: 170, render: (r) => <Celda valor={r.Seguros?.nombre} /> },
        {
            key: "valor",
            titulo: "Valor responsabilidad",
            alinear: "derecha",
            ancho: 150,
            render: (r) => <Celda valor={money(num(r.precio) * num(r.cantidad))} mono />,
        },
        {
            key: "estado",
            titulo: "Estado",
            ancho: 130,
            render: (r) => <ChipEstado tono={TONO_ESTADO[r.estado] || "pendiente"}>{r.estado}</ChipEstado>,
        },
        {
            key: "accion",
            titulo: "Acción",
            ancho: 140,
            render: (r) =>
                r.estado === "Por Cobrar" && hasPermission("billing.cancel") ? (
                    <Boton
                        tamano="sm"
                        variante="destructivo"
                        icono={IconoProhibido}
                        onClick={() => cancelar(r)}
                        data-testid={`pf-cancelar-${r.idprocedimiento_cita_paciente}`}
                    >
                        Cancelar
                    </Boton>
                ) : null,
        },
    ];

    const cancelar = async (r) => {
        // El diálogo con motivo obligatorio y validación se mantiene en Swal a
        // propósito: sustituirlo es reescribir la validación de un formulario
        // que cancela una responsabilidad de dinero, y eso entra con el patrón
        // Formulario, no con un cambio visual.
        const { value: motivo } = await Swal.fire({
            title: "Cancelar responsabilidad de seguro",
            input: "textarea",
            inputLabel: `${r.ExamenTipo?.descripcion || ""} · ${r.Seguros?.nombre || ""}`,
            inputPlaceholder: "Motivo (obligatorio, 3-250 caracteres)",
            inputAttributes: { maxlength: 250 },
            showCancelButton: true,
            confirmButtonText: "Cancelar responsabilidad",
            confirmButtonColor: rojoDelSistema(),
            inputValidator: (v) => (!v || v.trim().length < 3 ? "Ingrese un motivo (mín. 3 caracteres)" : null),
        });
        if (!motivo) return;
        const res = await CancelarResponsabilidadSeguro({ idprocedimiento_cita_paciente: r.idprocedimiento_cita_paciente, motivo });
        if (res.ok) {
            mostrar({ tipo: "exito", texto: res.message || "Responsabilidad cancelada." });
            fetchData(page, pageSize);
        } else {
            mostrar({ tipo: "error", texto: res.message || "No se pudo cancelar la responsabilidad." });
        }
    };

    const abrirConfirmacion = async () => {
        if (vendedores.length === 0) {
            const v = await ListaruserSinPagineo();
            if (v?.success) setVendedores(v.data.map((u) => ({ value: u.id, label: u.full_name })));
        }
        setVendedorId(null);
        setModo("DETALLADA");
        setInfoAdicional("");
        setConfirmOpen(true);
    };

    const emitir = async () => {
        setSubmitting(true);
        const res = await FacturarAseguradora({
            ids: selectedRows.map((r) => r.idprocedimiento_cita_paciente),
            vendedor_id: vendedorId,
            modo,
            info_adicional: infoAdicional.trim() || null,
        });
        setSubmitting(false);
        setConfirmOpen(false);

        if (res.ok) {
            // El número de la factura emitida NO se va en un aviso que se
            // desvanece: se queda en pantalla hasta que el usuario lo cierra.
            setEmitida(res.data || {});
            fetchData(page, pageSize);
        } else if (res.code === "SELECCION_CAMBIO") {
            mostrar({
                tipo: "aviso",
                texto: "Las responsabilidades cambiaron. Actualiza la lista y vuelve a seleccionar.",
            });
            fetchData(page, pageSize);
        } else {
            mostrar({ tipo: "error", texto: res.message || "No se pudo facturar a la aseguradora." });
        }
    };

    const puedeFacturar =
        hasPermission("billing.create") && resumen.count > 0 && resumen.segurosDistintos === 1;

    return (
        <>
            {resumen.count > 0 && (
                <Aviso
                    tipo={resumen.segurosDistintos === 1 ? "info" : "aviso"}
                    className="mb-2"
                    accion={
                        <Boton
                            tamano="sm"
                            variante="primario"
                            icono={IconoDocumentoHecho}
                            disabled={!puedeFacturar}
                            onClick={abrirConfirmacion}
                            data-testid="pf-facturar"
                        >
                            Facturar a aseguradora
                        </Boton>
                    }
                >
                    {resumen.segurosDistintos === 1 ? (
                        <span data-testid="pf-resumen">
                            Seleccionados: <b>{resumen.count}</b> · Aseguradora: <b>{resumen.aseguradora}</b> ·
                            Pacientes: {resumen.pacientes} · Total: <b>{money(resumen.total)}</b>
                        </span>
                    ) : (
                        "No puedes facturar responsabilidades de distintas aseguradoras juntas. Filtra por una aseguradora."
                    )}
                </Aviso>
            )}

            <DataTable
                columnas={columns}
                filas={rows}
                filaKey="idprocedimiento_cita_paciente"
                cargando={loading}
                error={error}
                onReintentar={() => fetchData(page, pageSize)}
                etiquetaTabla="Responsabilidades pendientes de facturar"
                textoVacioSinFiltros="No hay responsabilidades de seguro pendientes."
                seleccion={{
                    claves: selectedKeys,
                    onCambio: (keys, sel) => {
                        setSelectedKeys(keys);
                        setSelectedRows(sel);
                    },
                    // Sólo se puede facturar lo que está Por Cobrar.
                    deshabilitada: (r) => r.estado !== "Por Cobrar",
                    etiquetaFila: (r) =>
                        `Seleccionar ${r.ExamenTipo?.descripcion || "responsabilidad"} de ${r.paciente?.nombres_completos || "paciente"}`,
                }}
                paginacion={{
                    page,
                    pageSize,
                    total,
                    tamanos: [20, 50, 100],
                    onCambio: (p, ps) => {
                        setPage(p);
                        setPageSize(ps);
                        fetchData(p, ps);
                    },
                }}
            />

            {/* El diálogo de emisión (formato de detalle + vendedor) se mantiene
                tal cual: emite una factura real, y reescribirlo no es un cambio
                visual. Entra con el patrón Formulario. */}
            <Modal
                title="Facturar a aseguradora"
                open={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onOk={emitir}
                okText="Generar FAC"
                confirmLoading={submitting}
                okButtonProps={{ disabled: resumen.segurosDistintos !== 1 }}
            >
                <p>
                    ¿Generar <b>una FAC</b> a <b>{resumen.aseguradora}</b> por{" "}
                    <b>{resumen.count}</b> responsabilidades ({resumen.pacientes} pacientes),
                    total <b>{money(resumen.total)}</b>?
                </p>

                <div className="mt-2">
                    <label className="kc__etiqueta"><small>Formato de detalle</small></label>
                    <Radio.Group value={modo} onChange={(e) => setModo(e.target.value)} className="d-block">
                        <Radio value="DETALLADA">
                            Detallada — muestra cada prestación por separado ({resumen.lineasDetallada} líneas)
                        </Radio>
                        <Radio value="RESUMIDA">
                            Resumida — agrupa prestaciones del mismo servicio y precio ({resumen.lineasResumida} líneas)
                        </Radio>
                    </Radio.Group>
                    <small className="text-muted">La trazabilidad interna se conserva en ambos casos.</small>
                </div>

                <div className="mt-2">
                    <label className="kc__etiqueta"><small>Vendedor (opcional)</small></label>
                    <Select
                        className="w-100"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Sin vendedor"
                        options={vendedores}
                        value={vendedorId}
                        onChange={setVendedorId}
                    />
                </div>
                <div className="mt-2">
                    <label className="kc__etiqueta">
                        <small>Información adicional</small>{" "}
                        <small className="text-muted">Opcional</small>
                    </label>
                    <Input.TextArea
                        rows={3}
                        maxLength={300}
                        showCount
                        placeholder={`Si lo deja vacío se usará: "Facturacion a aseguradora: ${resumen.aseguradora || ""}"`}
                        value={infoAdicional}
                        onChange={(e) => setInfoAdicional(e.target.value)}
                        data-testid="facturar-info-adicional"
                    />
                    <small className="text-muted">
                        Aparece en la descripción del documento electrónico, así que es lo que verá la aseguradora
                        (n.º de convenio, período, planilla…).
                    </small>
                </div>

                <Input.TextArea className="mt-2" disabled value="La factura NO se envía a Contífico automáticamente. El envío al SRI se realiza aparte." autoSize />
            </Modal>

            {emitida && (
                <Modal
                    title="Factura generada"
                    open
                    onCancel={() => setEmitida(null)}
                    onOk={() => setEmitida(null)}
                    okText="Entendido"
                    cancelButtonProps={{ style: { display: "none" } }}
                >
                    <p data-testid="pf-emitida">
                        <b>{emitida.num_documento}</b> a {emitida.seguro}
                        <br />
                        Total: {money(emitida.total)} · {emitida.responsabilidades} responsabilidades ·{" "}
                        {emitida.lineas} líneas ({(emitida.modo || "").toLowerCase()})
                    </p>
                </Modal>
            )}
        </>
    );
}

export default GridFacturaListadoPorCobrar;
